import { Skeleton } from 'antd'

import { Volume } from '@/api/type-server'
import { Button, Divider, Trans } from '@/components'
import { useDomain } from '@/contexts/DomainContext'
import { Machine } from '@/models/machine'

import { DisabledActionTooltip, SelectMachineTooltip } from './SelectMachineTooltip'

// GPU models that do not support datadrive mounting
const MOUNT_DISABLED_GPU_MODELS = ['NVIDIA RTX PRO 6000']

// Section configuration interface
export interface MountDriveSectionConfig {
  titleI18nKey: string
  titleDefault: string
  showOwner?: boolean
  ownerLabel?: string
  getVolumeQuota: (volume: Volume) => number
  checkGpuRestriction?: boolean
  noDriveRegionMap?: Record<number, string>
  noVolumeNavigatePath?: string
  noVolumeI18nKey?: string
  noVolumeDefault?: string
  noVolumeButtonI18nKey?: string
  noVolumeButtonDefault?: string
  mountButtonI18nKey?: string
  mountButtonDefault?: string
}

// Pre-defined configurations
export const PERSONAL_SECTION_CONFIG: MountDriveSectionConfig = {
  titleI18nKey: 'create.mount',
  titleDefault: 'Mount Datadrive',
  showOwner: false,
  getVolumeQuota: (vol) => vol.quota_gb,
  checkGpuRestriction: true,
  noDriveRegionMap: { 3: 'JP-01' },
  noVolumeNavigatePath: '/space',
  mountButtonI18nKey: 'create.mountAsANetworkDrive',
  mountButtonDefault: 'Mount',
}

export const TEAM_SECTION_CONFIG: MountDriveSectionConfig = {
  titleI18nKey: 'create.mountTeamDatadrive',
  titleDefault: 'Mount Team Datadrive',
  showOwner: true,
  ownerLabel: 'Team',
  getVolumeQuota: (vol) => vol.quota_gb,
  checkGpuRestriction: false,
  noVolumeI18nKey: 'create.noTeamDatadriveAvailable',
  noVolumeDefault: '* No team datadrive available in this region',
  mountButtonI18nKey: 'create.mountTeamDatadriveButton',
  mountButtonDefault: 'Mount Team Datadrive',
}

export interface MountDriveSectionProps<T extends Volume = Volume> {
  machine: Machine | undefined
  mountVolume: T | undefined
  mountPath: string
  readWriteMode: boolean
  volumes: T[]
  loading?: boolean
  config: MountDriveSectionConfig
  setMountModalOpen: (open: boolean) => void
  resetMountConfig: () => void
  navigate?: (path: string) => void
}

export function MountDriveSection<T extends Volume = Volume>({
  machine,
  mountVolume,
  mountPath,
  readWriteMode,
  volumes,
  loading,
  config,
  setMountModalOpen,
  resetMountConfig,
  navigate,
}: MountDriveSectionProps<T>) {
  const { domainInfos } = useDomain()

  const {
    titleI18nKey,
    titleDefault,
    showOwner,
    ownerLabel,
    getVolumeQuota,
    checkGpuRestriction,
    noDriveRegionMap,
    noVolumeNavigatePath,
    noVolumeI18nKey,
    noVolumeDefault,
    noVolumeButtonI18nKey,
    noVolumeButtonDefault,
    mountButtonI18nKey,
    mountButtonDefault,
  } = config

  const hasValidVolumes = volumes.some((v) => getVolumeQuota(v) > 0)

  // Check if the selected GPU model is restricted from mounting
  const isGpuRestricted =
    checkGpuRestriction &&
    Boolean(
      machine?.unitSpec?.gpuName && MOUNT_DISABLED_GPU_MODELS.includes(machine.unitSpec.gpuName),
    )

  return (
    <div className="mt-8 px-8">
      <div className="t-sb mb-2 text-secondary">
        <Trans i18nKey={titleI18nKey}>{titleDefault}</Trans>
      </div>

      {mountVolume ? (
        <div className="bg-panel-gray-100 rounded-lg py-4 t-sm text-tertiary ">
          <div className="flex flex-wrap gap-y-4">
            <div className="w-1/4 px-4">
              <div className="text-secondary">Datadrive</div>
              <div className="text-primary">{(mountVolume as any).res_id ?? '-'}</div>
            </div>
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderRegion">Region</Trans>
              </div>
              <div className="text-primary">
                {domainInfos.find((d) => d.domain === mountVolume.domain)?.displayName}
              </div>
            </div>
            {showOwner && (
              <div className="w-1/4 px-4">
                <div className="text-secondary">Owner</div>
                <div className="text-primary">{ownerLabel || '-'}</div>
              </div>
            )}
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderUsage">Usage</Trans>
              </div>
              <div className="text-primary t-sb flex">
                {parseFloat((mountVolume.usage_mb / 1024 || 0).toFixed(3))}
                <div className="text-tertiary">/{getVolumeQuota(mountVolume)}GB</div>
              </div>
            </div>
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderMountPath">Mount Path</Trans>
              </div>
              <div className="text-primary">{mountPath}</div>
            </div>
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderPermissions">Permissions</Trans>
              </div>
              <div className="text-primary">{readWriteMode ? 'Read only' : 'Read & write'}</div>
            </div>
          </div>

          {/* action buttons */}
          <div className="px-4">
            <Divider className="my-4" />
            <div className="flex gap-4">
              <div
                className="text-btn-secondary cursor-pointer"
                onClick={() => setMountModalOpen(true)}
              >
                <Trans i18nKey="create.volumeTableHeaderEdit">Edit</Trans>
              </div>
              <div
                className="text-red-500 cursor-pointer"
                onClick={() => {
                  resetMountConfig()
                }}
              >
                <Trans i18nKey="create.volumeTableHeaderUnmount">Unmount</Trans>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // not mounted
        <>
          {/* Loading skeleton */}
          {machine && loading && <Skeleton.Button active style={{ width: 200, height: 40 }} />}

          {(!machine ||
            (machine && !loading && volumes.length > 0 && hasValidVolumes && !isGpuRestricted)) && (
            <SelectMachineTooltip enabled={!machine}>
              <Button
                type="outline"
                onClick={() => setMountModalOpen(true)}
                disabled={!machine || isGpuRestricted}
              >
                <Trans i18nKey={mountButtonI18nKey}>{mountButtonDefault}</Trans>
              </Button>
            </SelectMachineTooltip>
          )}

          {machine && isGpuRestricted && (
            <DisabledActionTooltip
              enabled
              title={
                <Trans i18nKey="create.mountGpuNotSupportedTooltip">
                  Datadrive mounting is not currently supported for this GPU model. This feature
                  will be available soon.
                </Trans>
              }
            >
              <Button type="outline" disabled>
                <Trans i18nKey={mountButtonI18nKey}>{mountButtonDefault}</Trans>
              </Button>
            </DisabledActionTooltip>
          )}

          {machine &&
            !isGpuRestricted &&
            !loading &&
            (volumes.length === 0 || !hasValidVolumes) && (
              <>
                {noDriveRegionMap && machine.domains.some((d) => noDriveRegionMap[d]) ? (
                  /* no datadrive in this region - upgrading */
                  <DisabledActionTooltip
                    enabled
                    title={
                      <Trans
                        i18nKey="create.mountAsNetworkDriveNotExistTooltip"
                        values={{
                          region: domainInfos.find((d) => d.domain === machine.domains[0])
                            ?.displayName,
                        }}
                      >
                        The Datadrive feature in region JP-01 is currently being upgraded. Stay
                        tuned!
                      </Trans>
                    }
                  >
                    <Button type="outline" disabled>
                      <Trans i18nKey={mountButtonI18nKey}>{mountButtonDefault}</Trans>
                    </Button>
                  </DisabledActionTooltip>
                ) : noVolumeNavigatePath && navigate ? (
                  /* no datadrive in this region, redirect to Space Management */
                  <>
                    <Button
                      type="outline"
                      onClick={() => {
                        navigate(noVolumeNavigatePath)
                      }}
                    >
                      <Trans i18nKey={noVolumeButtonI18nKey || 'create.spaceManagement'}>
                        {noVolumeButtonDefault || 'Space Management'}
                      </Trans>
                    </Button>
                    <div className="t-sm text-warning mt-2">
                      <Trans
                        i18nKey="create.mountAsNetworkDriveNotAvailable"
                        values={{
                          region: domainInfos.find((d) => d.domain === machine.domains[0])
                            ?.displayName,
                        }}
                      >
                        * There are currently no available Datadrives in this region. Please manage
                        your storage space.
                      </Trans>
                    </div>
                  </>
                ) : (
                  /* simple message for team drives */
                  <div className="t-sm text-warning">
                    <Trans i18nKey={noVolumeI18nKey || 'create.noTeamDatadriveAvailable'}>
                      {noVolumeDefault || '* No team datadrive available in this region'}
                    </Trans>
                  </div>
                )}
              </>
            )}
        </>
      )}
    </div>
  )
}
